import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Webhook {
  id: string
  name: string
  url: string
  events: string[]
  status: string
  last_triggered: string | null
}

interface Delivery {
  id: string
  webhook_id: string
  event: string
  status: string
  response_code: number
  response_time_ms: number
  timestamp: string
  retries: number
  error?: string
}

export default function WebhookManagerPage() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [deliveries, setDeliveries] = useState<Delivery[]>([])
  const [eventTypes, setEventTypes] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [whRes, delRes, evtRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/webhook-manager/webhooks'),
      fetch(API_BASE + '/webhook-manager/deliveries'),
      fetch(API_BASE + '/webhook-manager/event-types'),
      fetch(API_BASE + '/webhook-manager/statistics')
    ])
    if (whRes.ok) setWebhooks((await whRes.json()).webhooks || [])
    if (delRes.ok) setDeliveries((await delRes.json()).deliveries || [])
    if (evtRes.ok) setEventTypes((await evtRes.json()).event_types || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const testWebhook = async (id: string) => {
    const res = await fetch(API_BASE + '/webhook-manager/webhooks/' + id + '/test', { method: 'POST' })
    if (res.ok) alert('Test delivery sent!')
  }

  const retryDelivery = async (id: string) => {
    await fetch(API_BASE + '/webhook-manager/deliveries/' + id + '/retry', { method: 'POST' })
    fetchData()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'success': return 'bg-green-100 text-green-800'
      case 'disabled': case 'failed': return 'bg-red-100 text-red-800'
      case 'retrying': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Webhook Management</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Webhooks</div>
              <div className="text-2xl font-bold">{statistics.total_webhooks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_webhooks}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Deliveries (24h)</div>
              <div className="text-2xl font-bold">{statistics.deliveries_24h}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Success Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.success_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Response</div>
              <div className="text-2xl font-bold">{statistics.avg_response_time_ms}ms</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h2 className="text-xl font-semibold">Webhooks</h2>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Webhook</button>
              </div>
              <div className="divide-y">
                {webhooks.map(wh => (
                  <div key={wh.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedWebhook(wh)}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold">{wh.name}</div>
                        <div className="text-sm text-gray-500 font-mono">{wh.url}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(wh.status)}`}>{wh.status}</span>
                        <button onClick={(e) => { e.stopPropagation(); testWebhook(wh.id); }} className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200">Test</button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {wh.events.map(e => (
                        <span key={e} className="px-2 py-1 bg-gray-100 rounded text-xs">{e}</span>
                      ))}
                    </div>
                    {wh.last_triggered && <div className="text-xs text-gray-400 mt-2">Last triggered: {new Date(wh.last_triggered).toLocaleString()}</div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Deliveries</h2></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Event</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Response</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Time</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {deliveries.slice(0, 10).map(del => (
                    <tr key={del.id}>
                      <td className="px-4 py-2 text-sm">{del.event}</td>
                      <td className="px-4 py-2"><span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(del.status)}`}>{del.status}</span></td>
                      <td className="px-4 py-2 text-sm">{del.response_code} ({del.response_time_ms}ms)</td>
                      <td className="px-4 py-2 text-sm text-gray-500">{new Date(del.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-2">
                        {del.status === 'failed' && (
                          <button onClick={() => retryDelivery(del.id)} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200">Retry</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">Event Types</h3>
              <div className="space-y-2">
                {eventTypes.map(evt => (
                  <div key={evt.name} className="p-3 bg-gray-50 rounded">
                    <div className="font-medium text-sm">{evt.name}</div>
                    <div className="text-xs text-gray-500">{evt.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {selectedWebhook && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedWebhook.name}</h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-500">URL</div>
                    <div className="text-sm font-mono break-all">{selectedWebhook.url}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Events</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedWebhook.events.map(e => (
                        <span key={e} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{e}</span>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4">
                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Edit Webhook</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
